import React, { useState, useRef, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Logger from "../../logger";
import i18n from "../../localization/i18n";
import * as rk from "../../localization/resourceKeys";
import VerifyUser from "../components/resetPassword/verifyUser";
import VerifyChallenge from "../components/resetPassword/verifyChallenge";
import { StepLayout } from "../components/common/steplayout/StepLayout";
import Loader from "../components/common/loader/loader";
import SetPassword from "../components/resetPassword/setPassword";
import { SessionContext } from "../../context/sessionContext";
import styles from "../components/resetPassword/resetPassword.module.scss";

type ResetPasswordStep =
    | "verify-user"
    | "verify-challenge"
    | "set-password"
    | "success";

const MyVWResetPassword: React.FC = () => {
    const { sessionDetails } = useContext(SessionContext);
    const navigate = useNavigate();

    const ref = useRef<any>(null);

    const [step, setStep] = useState<ResetPasswordStep>("verify-user");
    const [submitPending, setSubmitPending] = useState(false);

    const [username, setUsername] = useState<string>("");
    const [ssn, setSSN] = useState<string>("");
    const [dob, setDOB] = useState<string>("");
    const [partyId, setPartyId] = useState<number | undefined>(undefined);
    const [challengeQuestion, setChallengeQuestion] = useState<string>("");
    const [challengeAnswer, setChallengeAnswer] = useState<string>("");
    const [isContinueEnabled, setIsContinueEnabled] =
        useState<boolean>(false);

    const isCanada =
        sessionDetails?.country?.toLowerCase() === "canada";
    const isAudi =
        sessionDetails?.brand?.toLowerCase() === "audi";
    const isVWCanada =
        sessionDetails?.brand?.toLowerCase() === "vw" && isCanada;

    const cancelAndReturnToLogin = (flowStep: number) => {
        Logger.info(
            `Exited Reset Password Flow in Step ${flowStep}`,
            "Location ==> /myVW/resetPassword",
            sessionDetails,
        );

        navigate("/myVW/login");
    };

    const continueToNextStep = (flowStep: number) => {
        Logger.info(
            `Clicked Continue in Reset Password Flow Step ${flowStep}`,
            "Location ==> /myVW/resetPassword",
            sessionDetails,
        );
    };

    useEffect(() => {
        document
            .getElementsByClassName("page-content")[0]
            ?.scrollTo(0, 0);
    }, [step]);

    if (step === "verify-user") {
        return (
            <StepLayout
                title={<h3>{i18n.t(rk.TITLE_RESET_PASSWORD)}</h3>}
                primaryAction={{
                    label: i18n.t(rk.BUTTON_CANCEL),
                    onClick: () => cancelAndReturnToLogin(1),
                }}
                secondaryAction={{
                    label: i18n.t(rk.BUTTON_CONTINUE),
                    disabled: !isContinueEnabled,
                    onClick: async () => {
                        setSubmitPending(true);

                        const verifyUser =
                            await ref.current?.verifyUserAction();

                        setSubmitPending(false);

                        if (verifyUser && isContinueEnabled) {
                            continueToNextStep(1);
                            setStep("verify-challenge");
                        }
                    },
                }}
            >
                <Loader isLoading={submitPending} />

                <VerifyUser
                    ref={ref}
                    setUsername={setUsername}
                    setSSN={setSSN}
                    setDOB={setDOB}
                    setPartyId={setPartyId}
                    setChallengeQuestion={setChallengeQuestion}
                    setIsContinueEnabled={setIsContinueEnabled}
                />
            </StepLayout>
        );
    }

    if (step === "verify-challenge") {
        return (
            <StepLayout
                title={<h3>{i18n.t(rk.TITLE_RESET_PASSWORD)}</h3>}
                primaryAction={{
                    label: i18n.t(rk.BUTTON_CANCEL),
                    onClick: () => cancelAndReturnToLogin(2),
                }}
                secondaryAction={{
                    label: i18n.t(rk.BUTTON_CONTINUE),
                    disabled: !isContinueEnabled,
                    onClick: async () => {
                        setSubmitPending(true);

                        const canTakeAction =
                            await ref.current?.verifyUserAction();

                        setSubmitPending(false);

                        if (canTakeAction) {
                            continueToNextStep(2);
                            setStep("set-password");
                        }
                    },
                }}
            >
                <Loader isLoading={submitPending} />

                <VerifyChallenge
                    ref={ref}
                    username={username}
                    ssn={ssn}
                    dob={dob}
                    challengeQuestion={challengeQuestion}
                    partyId={partyId}
                    setChallengeAnswer={setChallengeAnswer}
                    setIsContinueEnabled={setIsContinueEnabled}
                />
            </StepLayout>
        );
    }

    if (step === "set-password") {
        return (
            <StepLayout
                title={<h3>{i18n.t(rk.TITLE_RESET_PASSWORD)}</h3>}
                primaryAction={{
                    label: i18n.t(rk.BUTTON_CANCEL),
                    onClick: () => cancelAndReturnToLogin(3),
                }}
                secondaryAction={{
                    label: i18n.t(rk.BUTTON_CONTINUE),
                    disabled: !isContinueEnabled,
                    onClick: async () => {
                        setSubmitPending(true);

                        const canTakeAction =
                            await ref.current?.verifyUserAction();

                        setSubmitPending(false);

                        if (canTakeAction) {
                            continueToNextStep(3);
                            setStep("success");
                        }
                    },
                }}
            >
                <Loader isLoading={submitPending} />

                <SetPassword
                    ref={ref}
                    username={username}
                    challengeQuestion={challengeQuestion}
                    challengeAnswer={challengeAnswer}
                    setIsContinueEnabled={setIsContinueEnabled}
                />
            </StepLayout>
        );
    }

    return (
        <StepLayout
            title={<h3>{i18n.t(rk.TITLE_RESET_PASSWORD)}</h3>}
            secondaryAction={{
                label: i18n.t(rk.TITLE_LOGIN),
                onClick: () => {
                    Logger.info(
                        "Clicked login button",
                        "Location ==> /myVW/resetPassword",
                        sessionDetails,
                    );

                    navigate("/myVW/login");
                },
            }}
        >
            <p className={`subtitle ${isAudi && styles["subtitle-suc"]}`}>
                {isVWCanada
                    ? i18n.t(rk.TEXT_RESETPSWD_SUCCESS_VF)
                    : i18n.t(rk.TEXT_RESETPSWD_SUCCESS)}
            </p>
        </StepLayout>
    );
};

export default MyVWResetPassword;